import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import api from '../../utils/api';
import { progressHeatColor } from './okrColors';

export default function OkrMy({
  title = 'My OKRs',
  description = 'Objectives your manager assigned to you.',
  scoreboardMode = false
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/okr/my');
      setRows(r.data?.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {description}
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No OKRs assigned yet.</Typography>
      ) : scoreboardMode ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Objective</TableCell>
                <TableCell>Key Results category</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell align="right">Portfolio</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ assignment, okr }) => {
                const score = Number(okr?.computed_score || 0);
                const heat = progressHeatColor(score);
                return (
                  <TableRow key={assignment.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{okr.title}</Typography>
                    </TableCell>
                    <TableCell>{okr.category?.name || '—'}</TableCell>
                    <TableCell>{okr.period_label || '—'}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={`${score.toFixed(1)}%`}
                        sx={{ bgcolor: heat.bg, color: heat.fg, fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="right">{Number(okr.weighted_contribution || 0).toFixed(1)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<ViewIcon />} onClick={() => navigate(`/okr/objectives/${okr.id}`)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        rows.map(({ assignment, okr }) => {
          const score = Number(okr?.computed_score || 0);
          const heat = progressHeatColor(score);
          return (
            <Card key={assignment.id} variant="outlined" sx={{ mb: 2, borderRadius: 2, borderLeft: `4px solid ${heat.fg}` }}>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {okr.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {okr.category?.name} · {okr.period_label || 'Period TBD'}
                  </Typography>
                  {assignment.notes ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {assignment.notes}
                    </Typography>
                  ) : null}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Chip label={`Score ${score.toFixed(1)}%`} sx={{ bgcolor: heat.bg, color: heat.fg, fontWeight: 700 }} />
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    sx={{ display: 'block', mt: 1, ml: 'auto' }}
                    onClick={() => navigate(`/okr/objectives/${okr.id}`)}
                  >
                    Open
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );
}
